// client/src/components/business/ActionButtons.js
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../common/LoadingSpinner';

const ActionButton = memo(({
    type, // 'favorite' | 'report'
    isActive = false,
    isLoading = false,
    onClick,
    className = '',
    businessName,
    size = 'small', // 'small' | 'medium' | 'large'
    disabled = false
}) => {
    const { t } = useTranslation();

    const getIcon = () => {
        if (type === 'favorite') {
            if (isLoading) return null;
            return isActive ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 21s-6.716-4.35-9.193-7.23C1.104 12.07 1 10.223 1.91 8.89 3.35 6.78 6.34 6.24 8.28 7.9L12 11.14l3.72-3.24c1.94-1.66 4.93-1.12 6.37.99.91 1.333.806 3.18-.897 4.88C18.716 16.65 12 21 12 21z"/></svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12.001 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6.01 4.01 4 6.5 4c1.74 0 3.41.81 4.5 2.09C12.59 4.81 14.26 4 16 4 18.49 4 20.5 6.01 20.5 8.5c0 3.78-3.4 6.86-8.05 11.54l-.449.41zM6.5 6C5.12 6 4 7.12 4 8.5c0 3.04 2.86 5.64 8 10.05 5.14-4.41 8-7.01 8-10.05C20 7.12 18.88 6 17.5 6c-1.54 0-3.04.99-3.57 2.36h-1.86C10.54 6.99 9.04 6 7.5 6h-1z"/></svg>
            );
        }
        return (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M6 3h11l-1 4h3l-2 6H9l-1 4H6V3z"/></svg>
        );
    };

    const getAriaLabel = () => {
        if (type === 'favorite') {
            if (isLoading) return t('business.updatingFavorites', 'Updating favorites');
            const label = isActive ? t('business.removeFromFavorites') : t('business.addToFavorites');
            return businessName ? `${label} — ${businessName}` : label;
        }
        const label = t('business.reportIssue');
        return businessName ? `${label} — ${businessName}` : label;
    };

    const getTitle = () => {
        if (type === 'favorite') {
            if (isLoading) return t('common.updating', 'Updating...');
            const title = isActive ? t('business.removeFromFavorites') : t('business.addToFavorites');
            return businessName ? `${title} — ${businessName}` : title;
        }
        const title = t('business.reportIssue');
        return businessName ? `${title} — ${businessName}` : title;
    };

    const baseClassName = `${type}-btn-${size === 'small' ? 'small' : 'overlay'}`;
    const stateClasses = [
        isActive && type === 'favorite' && 'favorited',
        isLoading && 'loading',
        className
    ].filter(Boolean).join(' ');

    return (
        <button
            className={`${baseClassName} ${stateClasses}`.trim()}
            onClick={onClick}
            disabled={isLoading || disabled}
            aria-label={getAriaLabel()}
            aria-pressed={type === 'favorite' ? isActive : undefined}
            type="button"
            title={getTitle()}
        >
            {isLoading ? (
                <LoadingSpinner size="small" />
            ) : (
                <span aria-hidden="true">
                    {getIcon()}
                </span>
            )}
        </button>
    );
});

ActionButton.displayName = 'ActionButton';

export default ActionButton;

